import { create } from 'zustand';
import {
  Notification,
  NotificationCreateRequest,
  NotificationUpdateRequest,
  fetchNotificationsByRestaurant,
  createNotification,
  updateNotification,
  deleteNotification,
} from '../services/notifications/notificationsService';

interface NotificationsStore {
  // State
  notifications: Notification[];
  isLoading: boolean;
  error: string | null;
  currentRestaurantId: string | null;

  // Actions
  loadNotifications: (restaurantId: string) => Promise<void>;
  reloadNotifications: () => Promise<void>;
  isLoadedFor: (restaurantId: string) => boolean;

  createNotification: (request: NotificationCreateRequest) => Promise<Notification>;
  updateNotification: (request: NotificationUpdateRequest) => Promise<Notification>;
  deleteNotification: (id: string) => Promise<void>;

  clear: () => void;
}

export const useNotificationsStore = create<NotificationsStore>((set, get) => ({
  // State
  notifications: [],
  isLoading: false,
  error: null,
  currentRestaurantId: null,

  // Actions
  loadNotifications: async (restaurantId: string) => {
    const currentState = get();
    if (currentState.isLoading) return;

    set({ isLoading: true, error: null });

    try {
      const fetchedNotifications = await fetchNotificationsByRestaurant(restaurantId);
      set({
        notifications: fetchedNotifications,
        currentRestaurantId: restaurantId,
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Failed to load notifications:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load notifications'
      });
    }
  },

  reloadNotifications: async () => {
    const state = get();
    if (state.currentRestaurantId) {
      set({ currentRestaurantId: null });
      await get().loadNotifications(state.currentRestaurantId);
    }
  },

  isLoadedFor: (restaurantId: string) => {
    const state = get();
    return state.currentRestaurantId === restaurantId && !state.isLoading;
  },

  createNotification: async (request: NotificationCreateRequest) => {
    const notification = await createNotification(request);
    await get().reloadNotifications();
    return notification;
  },

  updateNotification: async (request: NotificationUpdateRequest) => {
    const notification = await updateNotification(request);
    await get().reloadNotifications();
    return notification;
  },

  deleteNotification: async (id: string) => {
    await deleteNotification(id);
    await get().reloadNotifications();
  },

  clear: () => {
    set({
      notifications: [],
      isLoading: false,
      error: null,
      currentRestaurantId: null
    });
  },
}));
